import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import MessagingService from '../services/messagingService';
import { doc, getDoc } from 'firebase/firestore';

const MessagingContext = createContext();

export const useMessaging = () => {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error('useMessaging must be used within a MessagingProvider');
  }
  return context;
};

export const MessagingProvider = ({ children, db, currentUser }) => {
  const [messagingService, setMessagingService] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [unreadCounts, setUnreadCounts] = useState({ internal: 0, client: 0, group: 0 });
  const [loading, setLoading] = useState(true);
  const [employeeData, setEmployeeData] = useState(null);

  // Récupérer les données employé depuis Firestore
  useEffect(() => {
    const fetchEmployeeData = async () => {
      if (!db || !currentUser?.uid) {
        setEmployeeData(null);
        return;
      }

      try {
        const employeeDoc = await getDoc(doc(db, 'employees', currentUser.uid));
        if (employeeDoc.exists()) {
          const data = { id: employeeDoc.id, ...employeeDoc.data() };
          setEmployeeData(data);
        } else {
          console.warn('Employee not found in Firestore');
          setEmployeeData(null);
        }
      } catch (error) {
        console.error('Erreur récupération données employé:', error);
        setEmployeeData(null);
      }
    };

    fetchEmployeeData();
  }, [db, currentUser?.uid]);

  // Initialiser le service de messagerie
  useEffect(() => {
    if (db) {
      const service = new MessagingService(db);
      setMessagingService(service);
    }
  }, [db]);

  // Écouter les conversations de l'utilisateur
  useEffect(() => {
    if (!messagingService || !employeeData?.id) return;

    setLoading(true);
    
    const unsubscribe = messagingService.listenToUserConversations(
      employeeData.id,
      (conversationsList) => {
        setConversations(conversationsList);
        setLoading(false);
      }
    );

    return unsubscribe;
  }, [messagingService, employeeData?.id]);

  // Écouter les messages de la conversation active avec gestion robuste
  useEffect(() => {
    if (!messagingService || !activeConversation) {
      console.log('[MessagingContext] ⚠️ Listener non activé:', { messagingService: !!messagingService, activeConversation: !!activeConversation });
      setMessages([]);
      return;
    }

    console.log('[MessagingContext] 🎬 Activation du listener pour:', activeConversation.id);

    let isSubscribed = true;
    let unsubscribe = null;

    const setupListener = () => {
      try {
        unsubscribe = messagingService.listenToMessages(
          activeConversation.id,
          (messagesList) => {
            if (!isSubscribed) return;
            
            console.log('[MessagingContext] 📬 Messages reçus dans le context:', messagesList.length);
            setMessages(messagesList);

            // Marquer comme lus quand on ouvre la conversation
            if (employeeData?.id) {
              messagingService.markConversationAsRead(activeConversation.id, employeeData.id);
            }
          }
        );
      } catch (error) {
        console.error('[MessagingContext] ❌ Erreur lors de la configuration du listener:', error);
        // Réessayer après un délai en cas d'erreur
        setTimeout(() => {
          if (isSubscribed) {
            console.log('[MessagingContext] 🔄 Tentative de reconnexion...');
            setupListener();
          }
        }, 2000);
      }
    };

    setupListener();

    return () => {
      console.log('[MessagingContext] 🔌 Déconnexion du listener pour:', activeConversation.id);
      isSubscribed = false;
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [messagingService, activeConversation?.id, employeeData?.id]);

  // Mettre à jour les compteurs de messages non lus
  const updateUnreadCounts = useCallback(async () => {
    if (!messagingService || !employeeData?.id) return;

    try {
      const counts = await messagingService.getUnreadCounts(employeeData.id);
      setUnreadCounts(counts);
    } catch (error) {
      console.error('Erreur mise à jour compteurs non lus:', error);
    }
  }, [messagingService, employeeData?.id]);

  // Mettre à jour les compteurs périodiquement
  useEffect(() => {
    if (!messagingService || !employeeData?.id) return;

    // Mettre à jour immédiatement
    updateUnreadCounts();

    // Mettre à jour toutes les 30 secondes
    const interval = setInterval(updateUnreadCounts, 30000);

    return () => clearInterval(interval);
  }, [messagingService, employeeData?.id, updateUnreadCounts]);

  // Mettre à jour les compteurs quand les conversations changent
  useEffect(() => {
    updateUnreadCounts();
  }, [conversations, updateUnreadCounts]);

  // Fonction pour envoyer un message
  const sendMessage = async (content) => {
    if (!messagingService || !activeConversation || !employeeData) {
      throw new Error('Service, conversation ou utilisateur manquant');
    }

    const senderName = `${employeeData.firstName} ${employeeData.lastName || ''}`.trim();
    return await messagingService.sendMessage(
      activeConversation.id,
      employeeData.id,
      senderName,
      content
    );
  };

  // Fonction pour démarrer une nouvelle conversation
  const startConversation = async (otherUserId, conversationType = null) => {
    if (!messagingService || !employeeData?.id) {
      throw new Error('Service ou utilisateur manquant');
    }

    // Déterminer le type de conversation si non fourni
    let type = conversationType;
    if (!type) {
      // Essayer de détecter automatiquement depuis les conversations existantes
      // ou vérifier dans Firestore
      try {
        const userDoc = await getDoc(doc(db, 'clients', otherUserId));
        type = userDoc.exists() ? 'client' : 'internal';
      } catch (error) {
        console.warn('Impossible de détecter le type, utilisation de internal par défaut');
        type = 'internal';
      }
    }

    const conversationId = await messagingService.findOrCreateConversation(
      employeeData.id,
      otherUserId,
      type
    );

    // Charger la conversation
    const conversation = conversations.find(conv => conv.id === conversationId) || {
      id: conversationId,
      participants: [employeeData.id, otherUserId],
      type: type
    };

    setActiveConversation(conversation);
    return conversationId;
  };

  // Fonction pour archiver une conversation
  const archiveConversation = async (conversationId) => {
    if (!messagingService || !employeeData?.id) return;

    await messagingService.archiveConversation(conversationId, employeeData.id);
    
    // Si c'est la conversation active, la désactiver
    if (activeConversation?.id === conversationId) {
      setActiveConversation(null);
      setMessages([]);
    }
  };

  // Fonction pour désarchiver une conversation
  const unarchiveConversation = async (conversationId) => {
    if (!messagingService) return;
    await messagingService.unarchiveConversation(conversationId);
  };

  // Fonction pour marquer une conversation comme lue
  const markAsRead = async (conversationId) => {
    if (!messagingService || !employeeData?.id) return;
    await messagingService.markConversationAsRead(conversationId, employeeData.id);
  };

  // Fonction pour créer un groupe
  const createGroup = async (groupName, groupDescription, participantIds) => {
    if (!messagingService || !employeeData?.id) {
      throw new Error('Service ou utilisateur manquant');
    }

    // Inclure le créateur dans les participants
    const allParticipants = Array.from(new Set([employeeData.id, ...participantIds]));

    // Construire le nom complet du créateur
    const creatorName = `${employeeData.firstName} ${employeeData.lastName || ''}`.trim() || 'Utilisateur';

    const conversationId = await messagingService.createGroupConversation(
      groupName,
      groupDescription,
      allParticipants,
      employeeData.id,
      creatorName
    );

    // Charger la conversation créée
    const conversation = conversations.find(conv => conv.id === conversationId) || {
      id: conversationId,
      participants: allParticipants,
      type: 'group',
      groupName,
      groupDescription
    };

    setActiveConversation(conversation);
    return conversationId;
  };

  // Fonction pour mettre à jour un groupe
  const updateGroup = async (conversationId, updates) => {
    if (!messagingService || !employeeData?.id) return;
    await messagingService.updateGroupInfo(conversationId, employeeData.id, updates);
  };

  // Fonction pour ajouter un participant à un groupe
  const addParticipant = async (conversationId, userId, userName) => {
    if (!messagingService || !employeeData?.id) return;
    await messagingService.addParticipantToGroup(conversationId, userId, employeeData.id, userName);
  };

  // Fonction pour retirer un participant d'un groupe
  const removeParticipant = async (conversationId, userId, userName) => {
    if (!messagingService || !employeeData?.id) return;
    await messagingService.removeParticipantFromGroup(conversationId, userId, employeeData.id, userName);
  };

  // Fonction pour quitter un groupe
  const leaveGroup = async (conversationId) => {
    if (!messagingService || !employeeData) return;

    const userName = `${employeeData.firstName} ${employeeData.lastName || ''}`.trim();
    await messagingService.leaveGroup(conversationId, employeeData.id, userName);

    // Si c'est la conversation active, la désactiver
    if (activeConversation?.id === conversationId) {
      setActiveConversation(null);
      setMessages([]);
    }
  };

  // Filtrer les conversations par type et statut d'archivage
  const getFilteredConversations = (type = 'internal', showArchived = false) => {
    return conversations.filter(conv =>
      conv.type === type &&
      conv.archived === showArchived
    );
  };

  const value = {
    // État
    conversations,
    activeConversation,
    messages,
    unreadCounts,
    loading,
    employeeData,

    // Actions
    setActiveConversation,
    sendMessage,
    startConversation,
    archiveConversation,
    unarchiveConversation,
    markAsRead,
    getFilteredConversations,
    updateUnreadCounts,

    // Actions pour les groupes
    createGroup,
    updateGroup,
    addParticipant,
    removeParticipant,
    leaveGroup,

    // Service
    messagingService
  };

  return (
    <MessagingContext.Provider value={value}>
      {children}
    </MessagingContext.Provider>
  );
};

export default MessagingContext;
